// Cadwork webgl Engine
// http://www.lexoview.com
//
// Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
// Dual licensed under the MIT and GPL
//
// @author Pavel Reznak (reznak@cadwork.cz)
// @co-author Jaroslav Pribyl (pribyl@cadwork.cz)
//
import View from "App/view/view";
import Renderer from "App/renderer";
import {mat4, vec2, vec3, vec4} from "gl-matrix";
import Camera from "App/view/cameras/camera";
import {project} from "App/utils/glu";
import Messenger from "App/messenger";
import CameraWebVR from "App/view/cameras/camera_web_vr";
import IScene from "js_interface/src/scene_interface";
import {CompassTypeEnum} from "js_interface/src/modules/hud/scene_hud_enums";

export default
class Hud {

    private _sceneAPI: IScene;
    private _view: View;
    private _renderer: Renderer;
    private _aspectRatio: vec2 = vec2.fromValues(16, 9);
    private _hudSize: vec2 = vec2.create();
    private _projMatrix: mat4 = mat4.create();
    private _MVMatrix: mat4 = mat4.create();
    private _MVPMatrix: mat4 = mat4.create();
    private _eye: vec3 = vec3.fromValues(0, 0, 10);
    private _target: vec3 = vec3.fromValues(0, 0, 0);
    private _upVector: vec3 = vec3.fromValues(0, 1, 0);

    private _startAngle: number = 0;

    private _arrows: any = {
        visible: false,
        idRange: [-1, 0],
        minSize: vec2.fromValues(70, 70),
        margin: vec2.fromValues(0, 40),
        update: false
    };

    private _compass: any = {
        visible: false,
        id: -1,
        minSize: vec2.fromValues(70, 70),
        margin: vec2.fromValues(30, 60),
        position: vec3.create()
    };

    private _mouse: any = {
        down: false,
        timer: null,
        onID: 0,
        clickedID: 0
    };

    private _pgc: any = {
        windowPoint: vec4.create(),
        middlePos: vec2.create(),
        tmpMat4: mat4.create(),
        tmpVec3: vec3.create(),
        tmpVec3B: vec3.create(),
        tmpVec2: vec2.create()
    };

    constructor(aSceneAPI: IScene, aView: View) {
        this._sceneAPI = aSceneAPI;
        this._view = aView;
        this._renderer = this._view.getDocument().getRenderer();
    }

    /**
     * Handle mouse event
     *
     * @param {number} x
     * @param {number} y
     * @param {number} delta
     * @param {number} buttonState
     * @return {boolean} True if event was handled and should not be handled by other listeners
     */
    public onDrag(x: number, y: number, delta: number, buttonState: number): boolean {
        /* jshint unused:false */
        if (!this._mouse.down || buttonState !== 1) {
            return false;
        }

        if (this._mouse.clickedID < this._compass.id || this._mouse.clickedID > this._compass.id + 3) {
            return false;
        }

        if (this._mouse.clickedID !== this._compass.id && this._mouse.clickedID !== this._compass.id + 1) {
            return true;
        }

        let angle = this._getAngle(this._view.getCamera(), x - this._pgc.middlePos[0], y - this._pgc.middlePos[1]) - this._startAngle;
        this._view.getControls().rotate(angle, 0);
        this._view.getControls().restrictCameraToGround();
        this._view.viewpointChanged();

        return true;
    }

    /**
     * Handle mouse event
     *
     * @param {number} x
     * @param {number} y
     * @param {number} buttonState
     * @return {boolean} True if event was handled and should not be handled by other listeners
     */
    public onMousePress(x: number, y: number, buttonState: number): boolean {
        if (buttonState !== 1) {
            return false;
        }

        // Test if framebuffer is active and rendered
        let rg = this._renderer.getWebGLRenderGraph();
        let buffer = rg.getLayer('controls').getFramebuffers().get('controls');
        if (buffer === null) {
            return false;
        }

        // Get clicked location
        let id = buffer.readInt(this._view, x, y);

        // Test if mouse clicked into any control object
        if (id === 0 || id < this._compass.id || id > this._arrows.idRange[1]) {
            return false;
        }

        this._mouse.clickedID = id;
        this._mouse.down = true;
        switch (this._mouse.clickedID) {
            case this._compass.id:
            case this._compass.id + 1:
                let position = this._view.getPosition();
                let size = this._view.getSize();
                let vv = [position[0], position[1], size[0], size[1]];
                project(
                    this._pgc.windowPoint,
                    this._compass.position,
                    this._MVPMatrix,
                    vv
                );
                this._pgc.windowPoint[0] = Math.round(this._pgc.windowPoint[0]);
                this._pgc.windowPoint[1] = this._view.getSize()[1] - 1 - Math.round(this._pgc.windowPoint[1]);
                vec2.set(this._pgc.middlePos, this._pgc.windowPoint[0], this._pgc.windowPoint[1]);
                this._startAngle = this._getAngle(this._view.getCamera(), x - this._pgc.middlePos[0], y - this._pgc.middlePos[1]);
                break;
            case this._compass.id + 2:
                this._mouse.timer = setInterval(() => {
                    this._sceneAPI.viewModeModule.zoom(0.1);
                }, 25);
                break;
            case this._compass.id + 3:
                this._mouse.timer = setInterval(() => {
                    this._sceneAPI.viewModeModule.zoom(-0.1);
                }, 25);
                break;
            case this._arrows.idRange[0] + 0:
            case this._arrows.idRange[0] + 3:
                this._sceneAPI.viewModeModule.setRightView(this._sceneAPI.activityModule.getActiveObject());
                break;
            case this._arrows.idRange[0] + 1:
            case this._arrows.idRange[0] + 5:
                this._sceneAPI.viewModeModule.setTopView(this._sceneAPI.activityModule.getActiveObject());
                break;
            case this._arrows.idRange[0] + 2:
            case this._arrows.idRange[0] + 4:
                this._sceneAPI.viewModeModule.setBackView(this._sceneAPI.activityModule.getActiveObject());
                break;

            default:
                return false;
        }

        this._view.viewpointChanged();

        return true;
    }

    /**
     * Handle mouse event
     *
     * @param {number} x
     * @param {number} y
     * @param {number} buttonState
     * @param {number} prevButton
     * @return {boolean} True if event was handled and should not be handled by other listeners
     */
    public onMouseRelease(x: number, y: number, buttonState: number, prevButton: number): boolean {
        /* jshint unused:false */
        if (!this._mouse.down || prevButton !== 1) {
            return false;
        }

        this._mouse.down = false;
        clearInterval(this._mouse.timer);
        this._mouse.clickedID = 0;

        return true;
    }

    /**
     * @param {number} x
     * @param {number} y
     * @param {number} buttonState
     */
    public onMove(x: number, y: number, buttonState: number): boolean {
        /* jshint unused:false */
        // Test if framebuffer is active and rendered
        let rg = this._renderer.getWebGLRenderGraph();
        let buffer = rg.getLayer('controls').getFramebuffers().get('controls');
        if (buffer === null) {
            return false;
        }

        // Get clicked location
        let before = this._mouse.onID;
        this._mouse.onID = buffer.readInt(this._view, x, y);
        if (this._mouse.onID !== before) {
            this._view.getViewMode().scheduleRedraw();
        }

        return false;
    }

    public onClick(x: number, y: number, buttonState: number, aDraggedDuringClick: boolean): boolean {
        return this._isOnCompass() || this._isOnAxisCross(); // do not continue consume event when the double click was on compass
    }

    /**
     * @param {number} x
     * @param {number} y
     * @return {boolean}
     */
    public onDoubleClick(x: number, y: number): boolean {
        return this._isOnCompass(); // do not continue consume event when the double click was on compass
    }

    public getClickedID(): number {
        return this._mouse.clickedID;
    }

    public getHoverID(): number {
        return this._mouse.onID;
    }

    public isArrowsEnabled(): boolean {
        return this._sceneAPI.hudModule.getAxesCrossIsEnabled();
    }

    public isArrowsVisible(): boolean {
        return this._arrows.visible;
    }

    public isCompassEnabled(): boolean {
        return this._sceneAPI.hudModule.getCompassIsEnabled();
    }

    public isCompassVisible(): boolean {
        return this._compass.visible;
    }

    public isCompassTiltEnabled(): boolean {
        return this._sceneAPI.hudModule.getCompassTiltIsEnabled();
    }

    /**
     * Margins from top right corner in pixels
     *
     * @return {vec2}
     */
    public getArrowsMargin(): vec2 {
        return this._arrows.margin;
    }

    public setArrowsNeedUpdate(aValue: boolean) {
        this._arrows.update = aValue;
    }

    public getArrowsNeedUpdate(): boolean {
        return this._arrows.update;
    }

    /**
     * Margins from top right corner in pixels
     *
     * @return {vec2}
     */
    public getCompassMargin(): vec2 {
        return this._compass.margin;
    }

    /**
     * @param {vec2} aValue
     */
    public setArrowsMargin(aValue: vec2) {
        vec2.copy(this._arrows.margin, aValue);
        this.setArrowsNeedUpdate(true);
    }

    public setCompassID(aID: number) {
        this._compass.id = aID;
    }

    public setArrowsIDRange(aRange: number[]) {
        this._arrows.idRange = aRange;
    }

    /**
     * @param {vec3} aPos
     */
    public setCompassPosition(aPos: vec3) {
        vec3.copy(this._compass.position, aPos);

        this._view.getViewMode().scheduleRedraw();
    }

    /**
     * @param {number} aWidth
     * @param {number} aHeight
     */
    public setCompassMinSize(aWidth: number, aHeight: number) {
        vec2.set(this._compass.minSize, aWidth, aHeight);

        this._view.getViewMode().scheduleRedraw();
    }

    public testHUDItemsForVisibility() {
        // Test arrows size, ignore if smaller than limit
        let size = vec2.fromValues(0.55, 0.55);
        let arrowSize = this.getHUDPointToScreen(size);
        vec2.scale(arrowSize, arrowSize, 2);
        let minSize = this._getArrowsMinSize();
        if (arrowSize[0] < minSize[0] || arrowSize[1] < minSize[1]) {
            if (this._arrows.visible) {
                this._arrows.visible = false;
                Messenger.fireCDWKEvent(this._sceneAPI.getTargetTag(), "cwx3d_HUDArrows", {visible: false});
            }
        } else {
            if (!this._arrows.visible) {
                this._arrows.visible = true;
                Messenger.fireCDWKEvent(this._sceneAPI.getTargetTag(), "cwx3d_HUDArrows", {visible: true});
            }
        }

        // Test compass size, ignore if smaller than limit
        vec2.set(size, 0.65, 0.65);
        let compassSize = this.getHUDPointToScreen(size);
        vec2.scale(compassSize, compassSize, 2);
        minSize = this._getCompassMinSize();
        if (compassSize[0] < minSize[0] || compassSize[1] < minSize[1]) {
            if (this._compass.visible) {
                this._compass.visible = false;
                Messenger.fireCDWKEvent(this._sceneAPI.getTargetTag(), "cwx3d_HUDCompass", {visible: false});
            }
        } else {
            if (!this._compass.visible) {
                this._compass.visible = true;
                Messenger.fireCDWKEvent(this._sceneAPI.getTargetTag(), "cwx3d_HUDCompass", {visible: true});
            }
        }
    }

    /**
     * @param {vec2} aPoint     2D point in HUD size to pixels on screen
     */
    public getHUDPointToScreen(aPoint: vec2): vec2 {
        this.recalculateSize();
        vec2.divide(this._pgc.tmpVec2, this._view.getSizeWithoutDPR(), this._hudSize);
        vec2.multiply(this._pgc.tmpVec2, this._pgc.tmpVec2, aPoint);

        return this._pgc.tmpVec2;
    }

    /**
     * @param {vec2} aPoint     2D point in pixels to HUD coordinates
     * @return {vec2}
     */
    public getHUDScreenToPoint(aPoint: vec2): vec2 {
        vec2.divide(this._pgc.tmpVec2, this._hudSize, this._view.getSizeWithoutDPR());
        vec2.multiply(this._pgc.tmpVec2, this._pgc.tmpVec2, aPoint);

        return this._pgc.tmpVec2;
    }

    public getSize() {
        return this._hudSize;
    }

    /**
     * HUD Matrix
     *
     * @return {mat4}
     */
    public getHUDMatrix() {
        return this._MVPMatrix;
    }

    /**
     * Called everytime when the view was resized
     */
    public recalculateSize() {
        this._recalculateHUDSize();
        this._reCalculateHUDMatrix();
    }
    /**
     * @param {Camera} camera
     * @param {number} dx
     * @param {number} dy
     * @return {number}
     * @private
     */
    private _getAngle(camera: Camera | CameraWebVR, dx: number, dy: number): number {
        let dt = vec3.fromValues(dx, dy, 0);
        let vector = camera.getCameraDirection();

        let matrix = this._pgc.tmpMat4;
        let tilt = this.isCompassTiltEnabled();
        if (this._mouse.clickedID === this._compass.id + 1) {
            vec3.scale(vector, vector, -1);
        }

        if (tilt) {
            vec3.set(this._pgc.tmpVec3, 0, 0, 0);
            vec3.set(this._pgc.tmpVec3B, 0, 1, 0);
            mat4.lookAt(matrix, vector, this._pgc.tmpVec3, this._pgc.tmpVec3B);
            mat4.rotateX(matrix, matrix, -Math.PI / 2.0);

            if (this._mouse.clickedID === this._compass.id + 1) {
                mat4.rotateY(matrix, matrix, Math.PI);
            }
        } else {
            let angleX = Math.atan2(vector[2], vector[0]);
            mat4.identity(matrix);
            mat4.rotateZ(matrix, matrix, -angleX);

            if (this._mouse.clickedID === this._compass.id) {
                mat4.rotateY(matrix, matrix, Math.PI);
            }
        }

        vec3.transformMat4(dt, dt, matrix);
        return Math.atan2(dt[1], dt[0]);
    }

    private _isOnCompass(): boolean {
        if (this._compass.id < 0) {
            return false;
        }

        return this._mouse.onID >= this._compass.id && this._mouse.onID <= this._compass.id + 3;
    }

    private _isOnAxisCross(): boolean {
        if (!this.isArrowsEnabled() || !this._arrows.visible) {
            return false;
        }

        return this._mouse.onID >= this._arrows.idRange[0] && this._mouse.onID <= this._arrows.idRange[1];
    }

    /**
     * Minimum axis size in pixels
     *
     * @return {vec2}
     */
    private _getArrowsMinSize(): vec2 {
        return this._arrows.minSize;
    }

    /**
     * Minimum compass size in pixels
     *
     * @return {vec2}
     */
    private _getCompassMinSize(): vec2 {
        return this._compass.minSize;
    }

    private _reCalculateHUDMatrix() {
        // Prepare variables
        mat4.ortho(this._projMatrix, -this._hudSize[0] / 2.0, this._hudSize[0] / 2.0, -this._hudSize[1] / 2.0, this._hudSize[1] / 2.0, 0.1, 20.0);
        mat4.lookAt(this._MVMatrix, this._eye, this._target, this._upVector);
        mat4.multiply(this._MVPMatrix, this._projMatrix, this._MVMatrix);
    }

    private _recalculateHUDSize() {
        let screenSize = this._view.getSizeWithoutDPR();
        let scale = Math.min(
            screenSize[0] / this._aspectRatio[0],
            screenSize[1] / this._aspectRatio[1]
        );

        this._hudSize[0] = screenSize[0] / scale;
        this._hudSize[1] = screenSize[1] / scale;
    }
}
